import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal.tsx";
import {
  BackButton,
  Footer,
  Header,
  Page,
  PageContent,
  useLogout,
} from "@/components/page.tsx";
import { MissingProfileDataPrompt } from "@/components/missing-profile-data-modal.tsx";
import { ServiceCard } from "@/components/service-card.tsx";
import {
  NthPurchaseCampaignGrant,
  useKioskNthPurchaseCampaignHold,
} from "@/features/nth-purchase-campaign/nth-purchase-campaign-banner";
import { useShop } from "@/hooks/use-shop";
import { LoadingSpinner } from "@/components/spinner";
import { Button } from "@/components/ui/button.tsx";
import {
  clearBalanceTopupPaymentKey,
  saveBalanceTopupPaymentKey,
} from "@/features/payment/utils";
import { useActiveReservations } from "@/hooks/use-active-reservations";
import { useUser } from "@/hooks/use-user.ts";
import { ErrorModal } from "@/lib/api";
import { api } from "@/lib/server";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import { getFeatureFlags } from "@/lib/runtime-config";

export const Route = createFileRoute("/machine-type-selector-screen")({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    context.queryClient.removeQueries({ queryKey: ["cash-payment-status"] });
    context.queryClient.invalidateQueries({ queryKey: ["user"] });
  },
  loader: () => {
    sessionStorage.removeItem("payment");
    sessionStorage.removeItem("price");
    sessionStorage.removeItem("machine-type");
    clearBalanceTopupPaymentKey();
  },
});

function RouteComponent() {
  useAudioSlot("page.machine-type-selector");
  const { t } = useTranslation();
  const { showModal } = useMuiModal();
  const navigate = useNavigate();
  const { data: user } = useUser();
  const { handleLogout } = useLogout();
  const activeReservations = useActiveReservations();
  const { data: shop } = useShop();
  // a held nth-purchase-campaign discount replaces any coupon
  const { hold } = useKioskNthPurchaseCampaignHold(shop.shopId);

  const balanceReplenishMutation = useStartBalanceReplenish();

  return (
    <Page>
      <MissingProfileDataPrompt />
      <NthPurchaseCampaignGrant shopId={shop.shopId} />
      <Header
        showUserDetails
        backArrowHref={
          user ? (
            <BackButton label={t("Kijelentkezés")} onClick={handleLogout} />
          ) : (
            `/login-option-selector-screen`
          )
        }
      />
      <PageContent className={`px-10 max-w-5xl`}>
        {balanceReplenishMutation.isPending && <LoadingSpinner />}

        <h2 className={`font-helvetica font-bold text-6xl text-center mb-8`}>
          <Trans>Miben segíthetünk?</Trans>
        </h2>
        {!hold && (
          <div
            className={`grid grid-cols-3 gap-12 items-center justify-center mb-6`}
          >
            <p className={`font-fancy text-4xl text-center`}>
              <Trans>Van kuponod?</Trans>
            </p>
            <Button
              variant={`orange`}
              className={`text-2xl font-bold px-2 max-w-max mx-auto`}
              onClick={() => {
                void recordTrackedEvent({ eventType: "coupon_entry_opened" });
                navigate({
                  to: "/coupon-screen/{-$machineId}/{-$dryerBundleId}",
                  params: {},
                });
              }}
            >
              <Trans>Kupon megadása</Trans>
            </Button>
            <p className={`font-fancy text-4xl text-center`}>
              <Trans>Használd fel!</Trans>
            </p>
          </div>
        )}
        {(activeReservations.data?.length ?? 0) > 0 && (
          <div className={`flex gap-10 items-center justify-center mb-10`}>
            <div className="relative w-[313px]">
              <ServiceCard
                className="border-orange-500"
                img="/select-wash.svg"
                onClick={() => {
                  void recordTrackedEvent({
                    eventType: "reservation_selected",
                  });
                  navigate({
                    to: "/active-reservations-screen",
                  });
                }}
                title={
                  <div>
                    <div className="absolute -top-2 -right-2 bg-orange-500 text-white rounded-full w-10 h-10 flex items-center justify-center text-lg font-bold shadow-lg animate-bounce">
                      {activeReservations.data?.length}
                    </div>
                    {t("Indítható foglalásaim")}
                  </div>
                }
              />
            </div>
          </div>
        )}
        <div className={`flex gap-10 items-center justify-center mb-10`}>
          <ServiceCard
            img="/select-wash.svg"
            title={t("Mosás")}
            onClick={() => {
              void recordTrackedEvent({
                eventType: "machine_type_selected",
                payload: { machineType: "washing-machines" },
              });
              navigate(machineSelection("washing-machines"));
            }}
          />
          <ServiceCard
            img="/select-dry.svg"
            title={t("Szárítás")}
            onClick={() => {
              void recordTrackedEvent({
                eventType: "machine_type_selected",
                payload: { machineType: "drying-machines" },
              });
              navigate(machineSelection("drying-machines"));
            }}
          />
        </div>
        {user && (
          <div className={`flex gap-10 items-center justify-center`}>
            <ServiceCard
              img={`/select-balance.svg`}
              title={t("Egyenlegfeltöltés")}
              onClick={async () => {
                const answer = await showModal<boolean>(<CashAlertModal />);
                if (answer) {
                  void recordTrackedEvent({
                    eventType: "balance_topup_started",
                  });
                  balanceReplenishMutation.mutate();
                }
              }}
            />
            <ServiceCard
              img="/select-profile.svg"
              disabledInOfflineMode
              title={t("Adataim")}
              onClick={() => {
                navigate({
                  to: "/profile-edit-screen",
                });
              }}
            />
          </div>
        )}
      </PageContent>
      <Footer
        helpText={
          <Trans>
            Válaszd ki a megfelelő gombbal, hogy mit szeretnél, mosni vagy
            szárítani. Ha van kuponod, ne felejtsd felhasználni!
          </Trans>
        }
      />
    </Page>
  );
}

/** The washers or dryers to choose from: the one-screen payment flow while
 * the wash start guide is on, the old machine selector otherwise. */
function machineSelection(machineType: "washing-machines" | "drying-machines") {
  return getFeatureFlags().washStartGuide
    ? ({ to: "/payment/$machineType", params: { machineType } } as const)
    : ({
        to: "/machine-selector-screen/$machine-type",
        params: { "machine-type": machineType },
      } as const);
}

const CashAlertModal = () => {
  useAudioSlot("modal.balance-topup-warning");
  const { closeModal } = useMuiModal();
  return (
    <Modal>
      <ModalTitle>
        <Trans>Figyelmeztetés!</Trans>
      </ModalTitle>
      <ModalContent className={`px-14`}>
        <p className={`mb-6`}>
          <Trans>
            Egyenlegfeltöltésnél, miután behelyezted a papír és/vagy fémpénzt,
            már nincs lehetőséged a visszalépésre. A "Végeztem" gomb
            megnyomásával a bedobott pénz feltöltésre kerül az egyenlegedre.
          </Trans>
        </p>
        <div className="flex gap-6">
          <Button
            onClick={() => closeModal(false)}
            variant="outline"
            className={`px-6`}
          >
            <Trans>Mégsem</Trans>
          </Button>
          <Button
            onClick={() => {
              closeModal(true);
            }}
            size={`icon`}
            className={`px-2`}
          >
            <Trans>Értettem, tovább</Trans>
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export function useStartBalanceReplenish() {
  const navigate = useNavigate();
  const { showModal } = useMuiModal();

  return useMutation({
    mutationFn: async () => {
      const res = await api.startHopperTopup();
      console.log({ res });
      if (res.type === "error") {
        showModal(<ErrorModal errorKey={res.error} />);
        throw res.error;
      }
      return res.data?.paymentKey;
    },
    onError: () => {
      navigate({
        to: "/machine-type-selector-screen",
      });
    },
    onSuccess: (paymentKey) => {
      if (!paymentKey) {
        navigate({
          to: "/machine-type-selector-screen",
        });
        return;
      }

      saveBalanceTopupPaymentKey(paymentKey);
      navigate({
        to: "/balance-replenish-cash-screen",
      });
    },
  });
}
